import React, { useEffect, useState } from "react";
import { Question, X, Sparkle } from "@phosphor-icons/react";
import { pingHelpClick } from "./GuideHelpButton";

// Floating "?" help button shown on every in-app screen. On a visitor's first
// visit it auto-shows a one-time "New here? Take the tour" callout that opens
// the Guide. Dismissal + tour-taken are remembered in localStorage.
const SEEN_KEY = "pp_help_tour_seen";

const HelpLauncher = ({ activeTab, onOpenGuide }) => {
  const [showCallout, setShowCallout] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!localStorage.getItem(SEEN_KEY)) {
      const t = setTimeout(() => setShowCallout(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const markSeen = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch (e) { /* ignore */ }
    setShowCallout(false);
  };

  const takeTour = () => { markSeen(); pingHelpClick("guide", "tour"); onOpenGuide(); };

  // Hide the FAB while already on the Guide (it would be redundant there).
  if (activeTab === "guide") return null;

  return (
    <div className="fixed right-4 bottom-24 lg:bottom-6 z-[55] flex flex-col items-end gap-2" data-testid="help-launcher">
      {showCallout && (
        <div className="relative w-64 rounded-2xl bg-white shadow-[0_8px_40px_-8px_rgba(76,29,149,0.35)] ring-1 ring-violet-100 p-4 animate-in fade-in slide-in-from-bottom-2" data-testid="help-tour-callout">
          <button onClick={markSeen} data-testid="help-tour-dismiss"
            className="absolute top-2 right-2 p-1 text-zinc-400 hover:text-zinc-700 rounded-full hover:bg-zinc-100" aria-label="Dismiss">
            <X size={15} />
          </button>
          <div className="flex items-center gap-1.5 text-violet-700 font-bold text-sm mb-1">
            <Sparkle size={15} weight="fill" /> New here?
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed mb-3">
            Take a quick tour of how everything works — and how to get the best results.
          </p>
          <div className="flex items-center gap-2">
            <button onClick={takeTour} data-testid="help-tour-start"
              className="flex-1 px-3 py-2 rounded-full bg-violet-600 text-white text-xs font-semibold hover:bg-violet-500 transition-colors">
              Take the tour →
            </button>
            <button onClick={markSeen} className="px-3 py-2 rounded-full text-xs font-medium text-zinc-500 hover:text-zinc-800" data-testid="help-tour-later">
              Later
            </button>
          </div>
          <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white rotate-45 ring-1 ring-violet-100 border-r-0 border-b-0" />
        </div>
      )}

      <button onClick={() => { markSeen(); pingHelpClick("guide", "fab"); onOpenGuide(); }} data-testid="help-fab" title="Guide & help — how everything works"
        aria-label="Open the guide"
        className="w-12 h-12 rounded-full bg-zinc-900 text-white shadow-lg flex items-center justify-center hover:bg-violet-600 transition-all hover:scale-105">
        <Question size={22} weight="bold" />
      </button>
    </div>
  );
};

export default HelpLauncher;
